import {Component, effect, inject, linkedSignal, signal} from '@angular/core';
import {AuthenticationService} from "@services";
import {FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {ButtonComponent, InputComponent, ToastService} from "@components";
import {GalleryParticipationService, QuotationService} from "@api-services";
import {zip} from "rxjs";
import {TranslocoDirective} from "@jsverse/transloco";
import {MatExpansionModule} from '@angular/material/expansion';
import {TrashIconComponent} from "@icons";
import {MatDialog} from '@angular/material/dialog';
import {PromptDialogComponent} from "../../../../components/prompt-dialog/prompt-dialog.component";

@Component({
  selector: 'wun-cv',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    InputComponent,
    TranslocoDirective,
    ButtonComponent,
    MatExpansionModule,
    TrashIconComponent
  ],
  templateUrl: './cv.component.html',
  styleUrl: './cv.component.scss'
})
export class CvComponent {
  private readonly quotationService = inject(QuotationService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly participationService = inject(GalleryParticipationService);
  private readonly toastService = inject(ToastService);
  readonly dialog = inject(MatDialog);

  deleteItemId = signal(0)
  deleting = signal<'gallery' | 'quote' | undefined>(undefined)

  participation = linkedSignal(() => {
    return this.authenticationService.current()?.galleryParticipation || []
  })

  quotes = linkedSignal(() => {
    return this.authenticationService.current()?.quotations || []
  })

  formQuotes = new FormArray([new FormGroup({
    height: new FormControl<number | null>(null, Validators.required),
    width: new FormControl<number | null>(null, Validators.required),
    title: new FormControl<string | null>('', [Validators.required, Validators.minLength(2)]),
    technique: new FormControl('', Validators.required),
    quote: new FormControl(0, [Validators.required, Validators.minLength(2)]),
    id: new FormControl(0),
  })])

  formParticipation = new FormArray([
    new FormGroup({
      title: new FormControl('', Validators.required),
      gallery: new FormControl('', Validators.required),
      quote: new FormControl(0),
      id: new FormControl(0)
    })])

  constructor() {
    effect(() => {
      const quotes = this.quotes();
      const rows = quotes.map(d => this.getQuoteRow());
      this.formQuotes.removeAt(0);
      this.formQuotes.controls.push(...rows)
      this.formQuotes.patchValue(quotes);
    });

    effect(() => {
      const p = this.participation();
      const rows = p.map(d => this.getGalleryRow());
      this.formParticipation.controls.push(...rows);
      this.formParticipation.patchValue(p);
    });
  }

  saveQuotes() {
    if(this.formQuotes.invalid){
      this.toastService.addMessage({
        severity: 'error',
        title: 'Compila tutti i campi'
      })
      return;
    }
    const quote = (v: any) => {
      return {
        quote: Number(v.quote!),
        width: Number(v.width!),
        title: v.title!,
        height: Number(v.height!),
        technique: v.technique! || '',
        user: this.authenticationService.current()?.id!
      }
    }
    zip(this.formQuotes.controls.map(
      f => {
        const v = f.value;
        if (v.id) {
          return this.quotationService.quotationsUpdate({
            id: v.id,
            patchQuotationRequestDto: quote(v)
          })
        }
        return this.quotationService.quotationsCreate(
          {
            createQuotationRequestDto: quote(v)
          }
        )
      }
    )).subscribe(
      f => {
        this.toastService.addMessage({
          severity: 'success',
          title: 'Salvataggio avvenuto con successo, attendi la valutazione di wundart'
        })
      }
    )
  }

  getQuoteRow() {
    return new FormGroup({
      height: new FormControl<number | null>(0, Validators.required),
      width: new FormControl<number | null>(0, Validators.required),
      title: new FormControl<string | null>('', [Validators.required, Validators.minLength(2)]),
      technique: new FormControl('', [Validators.required, Validators.minLength(2)]),
      quote: new FormControl(0, Validators.required),
      id: new FormControl(0),
    })
  }

  addNewQuote() {
    this.formQuotes.controls.push(
      this.getQuoteRow()
    )
  }

  getGalleryRow() {
    return new FormGroup({
      title: new FormControl('', [Validators.required, Validators.minLength(2)]),
      gallery: new FormControl('', [Validators.required, Validators.minLength(2)]),
      quote: new FormControl(0),
      id: new FormControl(0)
    })
  }

  addNewParticipation() {
    this.formParticipation.controls.push(
      this.getGalleryRow()
    )
  }

  saveParticipation() {
    if(this.formParticipation.invalid) {
      return;
    }
    zip(this.formParticipation.controls.map(
      d => {
        const v = d.value;
        if (v.id)
          return this.participationService.galleryParticipationsUpdate({
            id: v.id,
            patchGalleryParticipationRequestDto: {
              quote: v.quote!,
              gallery: v.gallery!,
              title: v.title!,
              user: this.authenticationService.current()?.id!
            } as any
          })
        return this.participationService.galleryParticipationsCreate({
          createGalleryParticipationRequestDto: {
            gallery: v.gallery!,
            quote: 0,
            title: v.title!,
            user: this.authenticationService.current()?.id!
          } as any
        })
      }
    )).subscribe()
  }

  deleteQuote(id: number, index: number) {
    this.deleting.set('quote');
    this.deleteItemId.set(id)
    const dialogRef = this.dialog.open(PromptDialogComponent, {
      data: {title: 'Delete quote', question: 'Sicuro di voler cancellare?'},
    });

    dialogRef.afterClosed().subscribe(result => {
      if(result.delete){
        this.quotationService.quotationsDelete(
          {
            id
          }
        ).subscribe(d => {
          this.formQuotes.removeAt(index, {
            emitEvent: true
          })
          this.formQuotes.updateValueAndValidity()
        })
      }
    });
  }

  deleteGallery(id: number) {
    this.deleting.set('gallery');
    this.deleteItemId.set(id)
  }
}
